import type { EngineInput, EngineResult, ValidationMessage } from '@conciair/scheduler-core';
import { supabase, unwrap } from '../../data/supabase';
import { engine } from '../../engine/client';
import { toAssignmentRows } from '../../engine/mapping';
import { fetchSnapshot } from '../../engine/snapshot';
import type { TablesInsert } from '../../data/database.types';

/** Spec 5.3 progress modal: validate -> coverage -> assign -> store. */
export type GenerateStep = 'snapshot' | 'validate' | 'generate' | 'store' | 'done';

export type GenerateOutcome =
  | { kind: 'invalid'; input: EngineInput; errors: ValidationMessage[]; warnings: ValidationMessage[] }
  | { kind: 'engine-errors'; input: EngineInput; errors: string[]; warnings: string[] }
  | { kind: 'ok'; input: EngineInput; result: EngineResult; runId: string; hash: string; ms: number; engineVersion: string };

const CHUNK = 500;

/**
 * Generate Draft, spec 6.1. Everything the engine needs comes from one
 * snapshot RPC; the worker validates and generates; the run and its
 * assignments are stored; the org points at the new draft.
 */
export async function generateDraft(orgId: string, onStep: (s: GenerateStep) => void): Promise<GenerateOutcome> {
  onStep('snapshot');
  await assertCurrentEngine();
  const { input, hash } = await fetchSnapshot(orgId);

  onStep('validate');
  const validation = await engine.validate(input);
  if (validation.errors.length > 0) {
    return { kind: 'invalid', input, errors: validation.errors, warnings: validation.warnings };
  }

  onStep('generate');
  const [{ result, ms }, version] = await Promise.all([engine.generate(input), engine.version()]);
  if (!result.ok) {
    return { kind: 'engine-errors', input, errors: result.errors, warnings: result.warnings };
  }

  onStep('store');
  const [aircraft, pilots] = await Promise.all([
    unwrap(await supabase.from('aircraft').select('id, tail').eq('org_id', orgId)),
    unwrap(await supabase.from('pilots').select('id, code').eq('org_id', orgId)),
  ]);
  const ids = {
    aircraftIdByTail: Object.fromEntries(aircraft.map((a) => [a.tail, a.id])),
    pilotIdByCode: Object.fromEntries(pilots.map((p) => [p.code, p.id])),
  };

  const runInsert: TablesInsert<'schedule_runs'> = {
    org_id: orgId,
    kind: 'draft',
    input_snapshot: input as unknown as TablesInsert<'schedule_runs'>['input_snapshot'],
    input_hash: hash,
    engine_version: version.engineVersion,
    seed: input.seed,
    ok: true,
    errors: [],
    warnings: result.warnings,
    coverage_warnings: result.coverageWarnings as unknown as TablesInsert<'schedule_runs'>['coverage_warnings'],
    violations: result.violations as unknown as TablesInsert<'schedule_runs'>['violations'],
    utilization: (result.utilization ?? []) as unknown as TablesInsert<'schedule_runs'>['utilization'],
    carry_out: result.carryOut as unknown as TablesInsert<'schedule_runs'>['carry_out'],
    duration_ms: Math.round(ms),
  };
  const run = unwrap(await supabase.from('schedule_runs').insert(runInsert).select('id').single());

  const rows = toAssignmentRows(orgId, run.id, result, ids);
  for (let i = 0; i < rows.length; i += CHUNK) {
    const { error } = await supabase.from('schedule_assignments').insert(rows.slice(i, i + CHUNK));
    if (error) throw new Error(error.message);
  }

  const { error: ptrError } = await supabase.rpc('set_current_draft_run', { p_run_id: run.id });
  if (ptrError) throw new Error(ptrError.message);

  onStep('done');
  return { kind: 'ok', input, result, runId: run.id, hash, ms, engineVersion: version.engineVersion };
}

/**
 * A tab opened before a deploy keeps the engine it loaded. The build publishes
 * the engine stamp beside the bundle; when the two differ, a run would use
 * stale rules and its errors would mislead, so it stops here instead.
 */
async function assertCurrentEngine(): Promise<void> {
  let deployed: { coreHash?: string } | null = null;
  try {
    const res = await fetch('/engine-version.json', { cache: 'no-store' });
    if (res.ok) deployed = (await res.json()) as { coreHash?: string };
  } catch {
    return; // offline or dev server: nothing to compare against
  }
  if (!deployed?.coreHash) return;
  const loaded = await engine.version();
  if (deployed.coreHash !== loaded.coreHash) {
    throw new Error('The portal was updated after this page was opened. Reload the page, then generate again.');
  }
}
